/**
 * Workspaces: full multi-tab UI state persisted server-side per user.
 *
 * One named workspace at a time is "active". The auto-save hook
 * watches relevant slices and bulk-PUTs to /api/workspaces/{active}
 * on a debounce. On boot the App fetches the active workspace and
 * dispatches `hydrateWorkspace` — ui/tabs/ohlc/analysis slices all
 * listen and reset their byTab maps to match.
 *
 * `isHydrating` guards the auto-save: while we're replaying the
 * server state, the redux writes coming OUT of the hydrate must not
 * trigger a save back to the server (which would race and possibly
 * overwrite with a half-built state). The flag flips on at fetch
 * start and off after hydrateWorkspace fires.
 */
import {
  createAction,
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { api, ApiError } from "@/api/client";
import type {
  ActiveWorkspacePayload,
  WorkspaceListEntry,
  WorkspaceListResponse,
  WorkspacePayload,
} from "@/api/types";

interface WorkspacesState {
  list: WorkspaceListEntry[];
  /** Server-recorded active workspace name (what loads on boot).
   *  May differ from `currentName` briefly between "switch" dispatch
   *  and the server confirming the change. */
  activeName: string | null;
  /** The workspace the UI is currently editing. Auto-save targets
   *  this name. Set by hydrate / switch / create. */
  currentName: string | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  saving: boolean;
  /** True between fetchWorkspace.pending and hydrateWorkspace
   *  firing — the auto-save hook reads this to suppress its
   *  debounced PUTs while we're replaying server state. */
  isHydrating: boolean;
  /** Bumps once per successful hydrateWorkspace dispatch. The Chart
   *  watches this to know "the active tab's viewport just got
   *  replaced from the server, queue a restore" — without it, the
   *  initial workspace load has no tab-switch signal and the
   *  fresh-load fitContent would overwrite the saved viewport. */
  hydrateCounter: number;
  /** Last server revision we know about, per workspace name. Used
   *  by the autosave hook to attach an optimistic-concurrency
   *  token to every PUT; on a 409 we know our copy is stale and
   *  refetch instead of silently overwriting another session's
   *  edits. Updated on every fetch + successful save. */
  revisionByName: Record<string, number>;
  /** Set to the conflicting workspace name when a save 409s, so
   *  the UI can surface "this was edited elsewhere — refreshing".
   *  Cleared after the refetch hydrates fresh state. */
  conflictName: string | null;
  error: string | null;
}

const initialState: WorkspacesState = {
  list: [],
  activeName: null,
  currentName: null,
  status: "idle",
  saving: false,
  isHydrating: false,
  hydrateCounter: 0,
  revisionByName: {},
  conflictName: null,
  error: null,
};

/** Broadcast action that ui/tabs/ohlc/analysis slices subscribe to
 *  via extraReducers. Carries the full workspace payload so each
 *  slice can rebuild its byTab map atomically. Kept as a standalone
 *  action (not a thunk) so the cross-slice subscribers see exactly
 *  one dispatch with a stable type string. */
export const hydrateWorkspace = createAction<WorkspacePayload>(
  "workspaces/hydrate",
);

export const fetchWorkspaceList = createAsyncThunk<WorkspaceListResponse>(
  "workspaces/fetchList",
  async () => api.listWorkspaces(),
);

/** Fetch a named workspace AND broadcast a hydrate for it. The two
 *  are bundled because the only sensible thing to do after fetching
 *  is replace the in-memory state with it. */
export const fetchWorkspace = createAsyncThunk<
  { name: string; payload: WorkspacePayload },
  string
>("workspaces/fetch", async (name, { dispatch }) => {
  const payload = await api.getWorkspace(name);
  dispatch(hydrateWorkspace(payload));
  return { name, payload };
});

export const saveWorkspace = createAsyncThunk<
  { name: string; payload: WorkspacePayload },
  { name: string; payload: WorkspacePayload }
>("workspaces/save", async ({ name, payload }, { getState }) => {
  // Inject the optimistic-concurrency token from local state if
  // the caller didn't supply one explicitly. New workspaces send
  // 0 and the server treats that as "create or accept-first".
  // getState() is loosely typed (no RootState dep here to avoid
  // a slice→store import cycle); cast through unknown to the
  // shape we actually depend on.
  const wsState = (getState() as { workspaces: WorkspacesState }).workspaces;
  const knownRevision =
    payload.revision ?? wsState.revisionByName[name] ?? 0;
  const stamped: WorkspacePayload = { ...payload, revision: knownRevision };
  try {
    const saved = await api.saveWorkspace(name, stamped);
    return { name, payload: saved };
  } catch (err: unknown) {
    // 409 = our revision is stale: another of THIS user's sessions saved,
    // or two autosave PUTs overlapped (e.g. a structural + content save
    // fired close together while you keep drawing).
    //
    // We must NOT hydrate here. The old behaviour refetched the server copy
    // and replaced tabs/drawings with it — clobbering the in-progress
    // drawing (the "409 + page auto-refresh while drawing" bug). Workspaces
    // are per-user, so every conflicting session is the SAME user: the right
    // resolution is most-recent-action-wins (the live-sync dirty-guard
    // already does this). So: pull the server's current revision and re-save
    // OUR local state on top, preserving the drawing. The 409 becomes
    // invisible. Retry ONCE — a second 409 falls through and the next
    // autosave will try again, still without ever hydrating/clobbering.
    if (err instanceof ApiError && err.status === 409) {
      try {
        const server = await api.getWorkspace(name); // raw read, NOT a hydrate
        const retry: WorkspacePayload = {
          ...payload,
          revision: server.revision ?? 0,
        };
        const saved = await api.saveWorkspace(name, retry);
        return { name, payload: saved };
      } catch {
        // Retry failed — surface the original 409 (no hydrate; drawing kept).
      }
    }
    throw err;
  }
});

export const deleteWorkspace = createAsyncThunk<string, string>(
  "workspaces/delete",
  async (name) => {
    await api.deleteWorkspace(name);
    return name;
  },
);

export const setActiveWorkspaceOnServer = createAsyncThunk<
  ActiveWorkspacePayload,
  string | null
>("workspaces/setActive", async (name) => {
  return api.setActiveWorkspace({ name });
});

/** Create-then-activate convenience. Saves the supplied payload
 *  under the new name, marks it active on the server, refreshes the
 *  list. Returns the new name so the slice can set `currentName`
 *  from the reducer — more robust than depending on the caller. */
export const createWorkspace = createAsyncThunk<
  { name: string },
  { name: string; payload: WorkspacePayload }
>("workspaces/create", async ({ name, payload }, { dispatch }) => {
  await dispatch(saveWorkspace({ name, payload })).unwrap();
  await dispatch(setActiveWorkspaceOnServer(name)).unwrap();
  await dispatch(fetchWorkspaceList()).unwrap();
  return { name };
});

const workspacesSlice = createSlice({
  name: "workspaces",
  initialState,
  reducers: {
    /** Local-only: set the name the auto-save targets. Used right
     *  after createWorkspace / fetchWorkspace so the next save lands
     *  on the right key. */
    setCurrentName(state, action: PayloadAction<string | null>) {
      state.currentName = action.payload;
    },
    /** Forcefully clear the hydrating flag — used when the user
     *  cancels a load or the app boots without any saved workspace. */
    clearHydrating(state) {
      state.isHydrating = false;
    },
    /** Live-sync: adopt a broadcast revision WITHOUT hydrating. Used
     *  when a remote save lands while THIS session has unsaved local
     *  edits — hydrating would clobber the newer local intent (e.g. a
     *  timeframe change inside the autosave debounce snapping back),
     *  so we take the new OCC token instead and let our pending
     *  autosave land on top: for one user across their own sessions,
     *  the most recent action winning is the right semantics. */
    adoptRevision(
      state,
      action: PayloadAction<{ name: string; revision: number }>,
    ) {
      const cur = state.revisionByName[action.payload.name] ?? 0;
      if (action.payload.revision > cur) {
        state.revisionByName[action.payload.name] = action.payload.revision;
      }
    },
    /** Surfaces a "concurrent edit detected" banner. The autosave
     *  thunk dispatches this when the server 409s the PUT, just
     *  before refetching the canonical state. The banner stays up
     *  until the user dismisses it. */
    markWorkspaceConflict(state, action: PayloadAction<string>) {
      state.conflictName = action.payload;
    },
    dismissWorkspaceConflict(state) {
      state.conflictName = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkspaceList.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchWorkspaceList.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.list = action.payload.workspaces;
        state.activeName = action.payload.active;
      })
      .addCase(fetchWorkspaceList.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message ?? "Failed to list workspaces";
      })

      .addCase(fetchWorkspace.pending, (state) => {
        state.isHydrating = true;
        state.error = null;
      })
      .addCase(fetchWorkspace.fulfilled, (state, action) => {
        state.currentName = action.payload.name;
        state.activeName = action.payload.name;
        // Auto-save must stay off until the hydrate action has been
        // processed by all subscribing slices. clearHydrating is
        // dispatched by the autosave bootstrap effect once that
        // settles in the same tick.
        state.isHydrating = false;
        // Pull the server's current revision in. The next save
        // will include it; if anyone else has saved since, the
        // server's stored revision will already be higher and
        // they'll have to refetch first.
        state.revisionByName[action.payload.name] =
          action.payload.payload.revision ?? 0;
        // Successful fetch resolves any prior conflict for this
        // workspace — local state now matches the server.
        if (state.conflictName === action.payload.name) {
          state.conflictName = null;
        }
      })
      .addCase(fetchWorkspace.rejected, (state, action) => {
        state.isHydrating = false;
        state.error = action.error.message ?? "Failed to load workspace";
      })

      .addCase(saveWorkspace.pending, (state) => {
        state.saving = true;
      })
      .addCase(saveWorkspace.fulfilled, (state, action) => {
        state.saving = false;
        // Bump our cached revision to what the server returned —
        // every subsequent autosave will use it as the OCC token.
        state.revisionByName[action.payload.name] =
          action.payload.payload.revision ?? 0;
      })
      .addCase(saveWorkspace.rejected, (state, action) => {
        state.saving = false;
        // Skip stamping `error` on a revision conflict — the
        // conflict has its own dedicated banner (conflictName)
        // so we don't want a redundant generic error toast on
        // top of it. createAsyncThunk only stringifies the
        // thrown error, so we identify the 409 by the message
        // we attached to ApiError ("revision_conflict") or by
        // a literal "409" anywhere.
        const msg = action.error.message ?? "";
        if (msg === "revision_conflict" || /\b409\b/.test(msg)) {
          return;
        }
        state.error = msg || "Failed to save workspace";
      })

      .addCase(deleteWorkspace.fulfilled, (state, action) => {
        state.list = state.list.filter((w) => w.name !== action.payload);
        if (state.currentName === action.payload) state.currentName = null;
        if (state.activeName === action.payload) state.activeName = null;
      })

      .addCase(hydrateWorkspace, (state) => {
        // Bump so the Chart can detect this hydrate landed even when
        // active-tab didn't change (initial app boot is the common
        // case where there's no tab-switch signal to ride on).
        state.hydrateCounter += 1;
      })

      .addCase(setActiveWorkspaceOnServer.fulfilled, (state, action) => {
        state.activeName = action.payload.name;
        // Reflect the new active in the list so the highlight updates
        // without waiting for a re-fetch.
        state.list = state.list.map((w) => ({
          ...w,
          is_active: w.name === action.payload.name,
        }));
      })

      .addCase(createWorkspace.fulfilled, (state, action) => {
        // Always set currentName here so the chip immediately
        // reflects the new workspace, regardless of whether the
        // caller follows up with setCurrentName.
        state.currentName = action.payload.name;
        state.activeName = action.payload.name;
        state.error = null;
      })
      .addCase(createWorkspace.rejected, (state, action) => {
        state.error =
          action.error.message ?? "Failed to create workspace";
      });
  },
});

export const {
  setCurrentName,
  clearHydrating,
  adoptRevision,
  markWorkspaceConflict,
  dismissWorkspaceConflict,
} = workspacesSlice.actions;
export default workspacesSlice.reducer;
